/**
 * Barn Quilt Variant Picker
 * --------------------------------------------------------------------------
 * A custom element that handles image-based variant selection for products
 * whose variants are different visual designs (e.g. barn quilts).
 *
 * Why this is a custom element (and an external file):
 * ----------------------------------------------------
 * Shopify's quick-add/quick-view feature fetches the product section HTML and
 * injects it into a modal via innerHTML. Per the HTML spec, <script> tags
 * inserted via innerHTML do NOT execute. Putting the controller logic inline
 * in the section would silently break the picker inside the quick-add modal.
 *
 * Custom elements solve this: as long as the class is registered on the page,
 * the browser will instantiate any matching tag (and run connectedCallback)
 * whenever it appears in the DOM — including elements injected via innerHTML.
 *
 * Mobile robustness:
 * ------------------
 * Some mobile browsers (notably iOS Safari) intermittently suppress the
 * `change` event on label-wrapped visually-hidden radios when other handlers
 * in the bubble path call preventDefault(). Dawn's product-form.js touches a
 * lot of form events. We defend against this by ALSO listening for `click`
 * on the labels and programmatically checking the radio + dispatching a
 * synthetic change event. The change handler is idempotent (dedupes by
 * variantId), so double-firing is harmless.
 *
 * Gallery robustness:
 * -------------------
 * Dawn applies the HTML `hidden` attribute to gallery <li>s when
 * `gallery_layout` is anything other than "stacked". `hidden` is `display:none`
 * at the user-agent level. To make sure our selected variant image and our
 * "keep:" lifestyle images actually display, we both (a) actively remove the
 * `hidden` attribute from items we want visible, and (b) ship CSS rules that
 * explicitly set `display: block !important` on those tagged items.
 *
 * We also watch the gallery wrapper with a MutationObserver. If Dawn's
 * <media-gallery> element restructures items after we tag them, we re-apply
 * state on the next frame.
 *
 * Debug mode:
 * -----------
 * Set `window.BQ_DEBUG = true` in the console, or add `?bq-debug=1` to the
 * URL, to enable verbose logging that shows exactly what the picker found
 * in the DOM and what classes it applied.
 *
 * For the quick-add modal to work even when the user lands directly on a
 * collection page, this script must be loaded on the collection page too.
 * Add to theme.liquid:
 *   <script src="{{ 'bq-variant-picker.js' | asset_url }}" defer></script>
 */

(function () {
  'use strict';

  if (window.customElements && customElements.get('bq-variant-picker')) return;

  var KEEP_PREFIX = 'keep:';

  // ---- Debug helpers ----
  function debugEnabled() {
    if (window.BQ_DEBUG === true) return true;
    try {
      return new URL(window.location.href).searchParams.get('bq-debug') === '1';
    } catch (_) { return false; }
  }
  function dlog() {
    if (!debugEnabled()) return;
    var args = Array.prototype.slice.call(arguments);
    args.unshift('[BQ]');
    console.log.apply(console, args);
  }

  class BqVariantPicker extends HTMLElement {
    constructor() {
      super();
      this._requestController = null;
      this._initialized = false;
      this._lastVariantId = null;
      this._galleryObserver = null;
      // Reentry guard: when we mutate the gallery ourselves, the observer
      // would otherwise fire and re-run us in a loop.
      this._applyingGalleryState = false;
    }

    connectedCallback() {
      if (this._initialized) return;
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this._init(), { once: true });
      } else {
        requestAnimationFrame(() => this._init());
      }
    }

    _init() {
      if (this._initialized) return;
      this._initialized = true;

      this.sectionId = this.dataset.sectionId || '';
      this.productUrl = this.dataset.productUrl || '';

      this.productInfo = this.closest('product-info');
      this.mediaModal = this.productInfo ? this.productInfo.querySelector('product-modal') : null;
      this.mediaWrapper = this.productInfo
        ? this.productInfo.querySelector('.product__media-wrapper')
        : null;

      dlog('init: sectionId=', this.sectionId, 'productInfo=', !!this.productInfo, 'mediaWrapper=', !!this.mediaWrapper);

      // Parse variant data JSON.
      this.variantData = [];
      this.variantMediaIds = [];
      var dataEl = this.querySelector('script[type="application/json"][data-bq-variant-data]');
      if (dataEl) {
        try {
          var parsed = JSON.parse(dataEl.textContent);
          this.variantData = Array.isArray(parsed.variants) ? parsed.variants : [];
          this.variantMediaIds = Array.isArray(parsed.variantMediaIds) ? parsed.variantMediaIds : [];
        } catch (e) {
          console.warn('[BQ] Variant JSON parse error:', e);
        }
      }
      dlog('variants:', this.variantData.length, 'variantMediaIds:', this.variantMediaIds);

      this.variantLookup = {};
      for (var i = 0; i < this.variantData.length; i++) {
        this.variantLookup[this.variantData[i].id] = this.variantData[i];
      }

      this.roomToggle = this.mediaWrapper ? this.mediaWrapper.querySelector('[data-bq-room-toggle]') : null;
      this._toggleRooms = () => {
        const expanded = this.roomToggle.getAttribute('aria-expanded') !== 'true';
        this.roomToggle.setAttribute('aria-expanded', String(expanded));
        this.roomToggle.textContent = expanded ? this.roomToggle.dataset.hideText : this.roomToggle.dataset.showText;
        this.mediaWrapper.classList.toggle('bq-rooms-expanded', expanded);
      };
      if (this.roomToggle) this.roomToggle.addEventListener('click', this._toggleRooms);

      this._onChange = this._onChange.bind(this);
      this._onClick = this._onClick.bind(this);
      this._onKeydown = this._onKeydown.bind(this);

      this.addEventListener('change', this._onChange);
      this.addEventListener('click', this._onClick);
      this.addEventListener('keydown', this._onKeydown);

      // Initial state.
      this._tagGalleryItems();
      var initial = this.querySelector('input.bq-variant-picker__radio:checked');
      if (initial) {
        this._lastVariantId = initial.value;
        this._showOnlyVariantMedia(initial.value);
      } else if (this.dataset.initialVariant) {
        this._lastVariantId = this.dataset.initialVariant;
        this._showOnlyVariantMedia(this._lastVariantId);
      }

      // Watch the gallery for restructuring by Dawn's <media-gallery> or other
      // scripts. If anything changes the gallery DOM after our setup, re-apply.
      this._setupGalleryObserver();
    }

    disconnectedCallback() {
      if (this.roomToggle) this.roomToggle.removeEventListener('click', this._toggleRooms);
      if (this._requestController) this._requestController.abort();
      this.removeEventListener('change', this._onChange);
      this.removeEventListener('click', this._onClick);
      this.removeEventListener('keydown', this._onKeydown);
      if (this._galleryObserver) {
        this._galleryObserver.disconnect();
        this._galleryObserver = null;
      }
      this._initialized = false;
    }

    _setupGalleryObserver() {
      if (!this.mediaWrapper || this._galleryObserver) return;
      var self = this;
      this._galleryObserver = new MutationObserver(function (mutations) {
        // Skip mutations we caused ourselves.
        if (self._applyingGalleryState) return;
        // Only re-apply if something structural happened (items added/removed
        // or the `hidden` attribute toggled). Class changes alone are fine —
        // we already drove those.
        var structural = mutations.some(function (m) {
          if (m.type === 'childList') return true;
          if (m.type === 'attributes' && m.attributeName === 'hidden') return true;
          return false;
        });
        if (!structural) return;
        dlog('gallery mutated, re-applying for variant', self._lastVariantId);
        if (self._lastVariantId) {
          requestAnimationFrame(function () {
            self._showOnlyVariantMedia(self._lastVariantId);
          });
        }
      });
      this._galleryObserver.observe(this.mediaWrapper, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['hidden']
      });
    }

    // ---- Event handlers ----

    _onClick(e) {
      var label = e.target.closest && e.target.closest('.bq-variant-picker__label');
      if (!label || !this.contains(label)) return;

      if (label.classList.contains('bq-variant-picker__label--unavailable')) {
        e.preventDefault();
        return;
      }

      var radio = label.querySelector('input.bq-variant-picker__radio');
      if (!radio || radio.disabled) return;
      if (radio.checked) return;

      var groupRadios = this.querySelectorAll('input.bq-variant-picker__radio[name="' + radio.name + '"]');
      for (var i = 0; i < groupRadios.length; i++) groupRadios[i].checked = false;
      radio.checked = true;
      radio.dispatchEvent(new Event('change', { bubbles: true }));
      e.preventDefault();
    }

    _onChange(e) {
      var radio = e.target && e.target.closest && e.target.closest('input.bq-variant-picker__radio');
      if (!radio || !this.contains(radio)) return;
      if (!radio.checked) return;

      var variantId = radio.value;
      if (this._lastVariantId === variantId) return;
      this._lastVariantId = variantId;

      this._updateSelection(variantId, radio);
    }

    _onKeydown(e) {
      var navKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'];
      if (navKeys.indexOf(e.key) === -1) return;
      if (!e.target.classList || !e.target.classList.contains('bq-variant-picker__radio')) return;

      var radios = Array.prototype.slice.call(
        this.querySelectorAll('input.bq-variant-picker__radio:not([disabled])')
      );
      if (radios.length === 0) return;

      var currentIdx = radios.indexOf(document.activeElement);
      if (currentIdx === -1) currentIdx = radios.findIndex(function (r) { return r.checked; });
      if (currentIdx === -1) return;

      var newIdx = currentIdx;
      if (e.key === 'ArrowRight' || e.key === 'ArrowDown') newIdx = (currentIdx + 1) % radios.length;
      if (e.key === 'ArrowLeft' || e.key === 'ArrowUp') newIdx = (currentIdx - 1 + radios.length) % radios.length;

      if (newIdx !== currentIdx) {
        e.preventDefault();
        var next = radios[newIdx];
        next.checked = true;
        next.focus();
        next.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }

    // ---- Selection update ----

    _updateSelection(variantId, radio) {
      var labels = this.querySelectorAll('.bq-variant-picker__label');
      for (var i = 0; i < labels.length; i++) {
        labels[i].classList.remove('bq-variant-picker__label--selected');
      }
      var targetLabel = radio.closest('.bq-variant-picker__label');
      if (targetLabel) targetLabel.classList.add('bq-variant-picker__label--selected');

      var variant = this.variantLookup[variantId];
      var soldNotice = this.querySelector('[data-bq-sold-link]');
      if (soldNotice) soldNotice.hidden = true;
      if (variant) {
        var scope = this.productInfo || document;
        var captionVal = scope.querySelector('[data-bq-caption-value]');
        if (captionVal) captionVal.textContent = variant.title;
        var currentVal = this.querySelector('[data-bq-current-value]');
        if (currentVal) currentVal.textContent = variant.title;
        var preview = this.querySelector('.bq-mobile-preview__image');
        if (preview) { preview.removeAttribute('srcset'); preview.src = variant.imageUrl; preview.alt = variant.title; }
      }

      if (this.productInfo) {
        var hidden = this.productInfo.querySelectorAll('input[name="id"]');
        for (var j = 0; j < hidden.length; j++) { hidden[j].value = variantId; hidden[j].dispatchEvent(new Event('change', { bubbles: true })); }
      }

      if (!this._isInModal()) {
        try {
          var newUrl = new URL(window.location.href);
          newUrl.searchParams.set('variant', variantId);
          window.history.replaceState({ variant: variantId }, '', newUrl.toString());
        } catch (urlErr) {
          window.history.replaceState(
            { variant: variantId },
            '',
            window.location.pathname + '?variant=' + variantId
          );
        }
      }

      this._showOnlyVariantMedia(variantId);
      this._fetchAndApply(variantId);
    }

    _isInModal() {
      return !!this.closest('quick-add-modal, .quick-add-modal, [data-modal], dialog, [role="dialog"]');
    }

    async _fetchAndApply(variantId) {
      if (!this.productUrl || !this.sectionId || !this.productInfo) return;
      if (this._requestController) this._requestController.abort();
      const controller = new AbortController();
      this._requestController = controller;
      const scope = this.productInfo;
      const form = scope.querySelector('product-form');
      const payment = scope.querySelector('.shopify-payment-button');
      const pickup = scope.querySelector('pickup-availability');
      if (form) { form.toggleSubmitButton(true); form.handleErrorMessage(); }
      if (payment) payment.hidden = true;
      if (pickup) pickup.hidden = true;
      this.setAttribute('aria-busy', 'true');
      try {
        const url = new URL(this.productUrl, location.origin);
        url.searchParams.set('variant', variantId);
        url.searchParams.set('section_id', this.productInfo.dataset.originalSection || this.sectionId);
        const response = await fetch(url, { signal: controller.signal });
        if (!response.ok) throw new Error('Variant request failed');
        const html = await response.text();
        if (controller.signal.aborted || this._lastVariantId !== variantId || !this.isConnected) return;
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const freshButton = doc.querySelector('product-form [type="submit"]');
        if (!freshButton) throw new Error('Missing purchase controls');
        if (typeof scope.updateMedia === 'function') {
          scope.updateMedia(doc, this.variantLookup[variantId]?.mediaId);
          this._showOnlyVariantMedia(variantId);
        }
        for (const prefix of ['price-', 'Inventory-', 'Sku-']) {
          const fresh = doc.querySelector('[id^="' + prefix + '"]');
          const current = scope.querySelector('[id^="' + prefix + '"]');
          if (fresh && current) { current.innerHTML = fresh.innerHTML; current.className = fresh.className; }
        }
        const unavailable = freshButton.disabled;
        if (form) {
          form.toggleSubmitButton(unavailable, freshButton.querySelector('span')?.textContent);
          form.submitButton.removeAttribute('aria-disabled');
        }
        if (payment) payment.hidden = unavailable;
        const newPickup = doc.querySelector('pickup-availability');
        if (pickup && newPickup) {
          newPickup.dataset.variantId = variantId;
          pickup.replaceWith(newPickup);
        }
        document.dispatchEvent(new CustomEvent('variant:changed', {detail: {variantId, sectionId: this.sectionId}}));
      } catch (error) {
        if (error.name === 'AbortError' || this._lastVariantId !== variantId || !this.isConnected) return;
        if (form) form.handleErrorMessage(this.dataset.updateError);
      } finally {
        if (this._requestController === controller) this.removeAttribute('aria-busy');
      }
    }

    // ---- Gallery management ----

    _tagGalleryItems() {
      if (!this.mediaWrapper) return [];

      var items = this.mediaWrapper.querySelectorAll(
        '.product__media-list > li, ul[id^="Slider-Gallery"] > li'
      );

      dlog('tagGalleryItems found', items.length, 'items');

      for (var i = 0; i < items.length; i++) {
        var item = items[i];
        var dataMediaId = item.getAttribute('data-media-id') || '';
        var itemId = item.getAttribute('id') || '';

        var isVariantMedia = dataMediaId.indexOf('-acs-scene-') !== -1;
        for (var j = 0; j < this.variantMediaIds.length; j++) {
          var vmid = this.variantMediaIds[j];
          if (dataMediaId.indexOf(vmid) !== -1 || itemId.indexOf(vmid) !== -1) {
            isVariantMedia = true;
            break;
          }
        }

        if (isVariantMedia) {
          item.classList.remove('bq-non-variant-media');
          item.classList.add('bq-variant-media');
        } else if (!item.classList.contains('bq-non-variant-media')) {
          item.classList.add('bq-non-variant-media');
          var img = item.querySelector('img');
          var alt = img ? (img.getAttribute('alt') || '').trim() : '';
          if (alt.toLowerCase().indexOf(KEEP_PREFIX) === 0) {
            item.classList.add('bq-media-keep');
            if (img) img.setAttribute('alt', alt.substring(KEEP_PREFIX.length).trim());
          }
        }
      }

      return Array.prototype.slice.call(items);
    }

    _showOnlyVariantMedia(variantId) {
      if (!this.mediaWrapper) {
        dlog('showOnlyVariantMedia: no mediaWrapper');
        return;
      }

      var variant = this.variantLookup[variantId];
      if (!variant) {
        console.warn('[BQ] No variant data for', variantId);
        return;
      }

      var allItems = this._tagGalleryItems();
      if (allItems.length === 0) {
        dlog('showOnlyVariantMedia: 0 gallery items');
        return;
      }

      var prefixedId = variant.prefixedMediaId;
      var rawId = variant.mediaId;

      var match = null;
      var scene = null;
      for (var i = 0; i < allItems.length; i++) {
        var dataMediaId = allItems[i].getAttribute('data-media-id') || '';
        var elemId = allItems[i].getAttribute('id') || '';
        if (dataMediaId.endsWith('-acs-scene-' + variantId)) scene = allItems[i];
        if (
          dataMediaId === prefixedId ||
          dataMediaId.endsWith('-' + rawId) ||
          elemId.endsWith('-' + rawId)
        ) {
          match = allItems[i];
        }
      }

      if (!match) {
        console.warn('[BQ] No gallery item matches variant media', rawId, 'prefixed:', prefixedId);
        return;
      }

      // Suspend the MutationObserver while we mutate (otherwise it'd see our
      // own attribute/class changes and re-fire us).
      this._applyingGalleryState = true;

      this.mediaWrapper.classList.add('bq-gallery-active');

      // Reset visible flags and orders on all items.
      for (var k = 0; k < allItems.length; k++) {
        allItems[k].classList.remove('bq-media-visible');
        allItems[k].style.order = '';
      }

      // Mark the selected variant visible AND remove [hidden] attribute that
      // Dawn applies when gallery_layout != "stacked". Without this, the
      // matched item stays display:none from the user-agent style on [hidden].
      match.classList.add('bq-media-visible');
      if (match.hasAttribute('hidden')) {
        match.removeAttribute('hidden');
        dlog('removed [hidden] from selected variant media', rawId);
      }
      if (scene) {
        scene.classList.add('bq-media-visible');
        scene.removeAttribute('hidden');
      }

      // Same treatment for "keep:" lifestyle images.
      var keepCount = 0;
      for (var x = 0; x < allItems.length; x++) {
        if (allItems[x].classList.contains('bq-media-keep')) {
          if (allItems[x].hasAttribute('hidden')) {
            allItems[x].removeAttribute('hidden');
          }
          keepCount++;
        }
      }

      // Order: selected variant first, then keep images in DOM order.
      var order = 0;
      if (scene) scene.style.order = String(order++);
      match.style.order = String(order++);
      var selectedImage = match.querySelector('img');
      if (selectedImage) selectedImage.alt = variant.title;
      for (var m = 0; m < allItems.length; m++) {
        if (allItems[m] !== match && allItems[m].classList.contains('bq-media-keep')) {
          allItems[m].style.order = String(order++);
        }
      }

      var keepIds = allItems.filter(function (item) { return item.classList.contains('bq-media-keep'); }).map(function (item) { return (item.dataset.mediaId || '').split('-').pop(); });
      var modal = this.mediaModal || this.productInfo.querySelector('product-modal');
      if (modal) {
        modal.querySelectorAll('[data-media-id]').forEach(function (item) {
          var id = item.dataset.mediaId;
          item.classList.toggle('bq-modal-hidden', id !== rawId && id !== 'acs-scene-' + variantId && keepIds.indexOf(id) === -1);
        });
      }

      dlog('applied: 1 variant + ' + keepCount + ' keep images visible (of ' + allItems.length + ' total)');

      // Re-enable the observer on the next frame to allow our mutations to
      // settle first.
      var self = this;
      requestAnimationFrame(function () {
        self._applyingGalleryState = false;
      });
    }
  }

  customElements.define('bq-variant-picker', BqVariantPicker);
})();
